import { useEffect, useState } from 'react';
import { Modal, Button, Input } from '@react-apps/design-system';
import { useAccountActions } from '../hooks/use-account-actions';

/** Gera uma senha temporária forte (admin comunica ao usuário por fora). */
function generatePassword(length = 14): string {
  const alphabet =
    'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*';
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

/**
 * Admin define uma senha temporária para a conta. O backend marca
 * `mustChangePassword` → o usuário é obrigado a trocá-la no próximo login.
 */
export function SetTemporaryPasswordModal({
  userId,
  accountId,
  userName,
  open,
  onClose,
}: {
  userId: string;
  accountId: string;
  userName: string;
  open: boolean;
  onClose: () => void;
}) {
  const { setTemporaryPassword } = useAccountActions(userId);
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (open) {
      setPassword(generatePassword());
      setTemporaryPassword.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const done = setTemporaryPassword.isSuccess;

  const handleConfirm = () => {
    setTemporaryPassword.mutate({ accountId, password });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Forçar troca de senha"
      description={`Define uma senha temporária (escolhida pelo admin) para ${userName}. No próximo login ele é obrigado a trocá-la por uma senha própria.`}
      size="md"
      footer={
        done ? (
          <Button onClick={onClose}>Fechar</Button>
        ) : (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={setTemporaryPassword.isPending || password.length < 8}
            >
              {setTemporaryPassword.isPending
                ? 'Definindo…'
                : 'Definir senha temporária'}
            </Button>
          </div>
        )
      }
    >
      <div className="space-y-4">
        {done ? (
          <div className="space-y-2 rounded-lg bg-green-50 p-3 text-sm text-green-700 dark:bg-green-950/40 dark:text-green-300">
            <p>Senha temporária definida. Comunique ao usuário:</p>
            <p className="font-mono text-base">{password}</p>
            <p className="text-xs">
              No próximo login o próprio usuário será levado a definir uma senha
              nova — esta aqui só serve para ele entrar.
            </p>
          </div>
        ) : (
          <>
            <div className="border-(--color-border) bg-(--color-bg-secondary) text-(--color-text-secondary) rounded-lg border p-3 text-xs">
              Senha temporária definida pelo admin e comunicada ao usuário por
              fora. Ela só serve para o primeiro acesso: ao entrar, o usuário é
              levado a trocá-la por uma senha própria antes de seguir.
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Input
                  label="Senha temporária"
                  id="temp-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="mínimo 8 caracteres"
                />
              </div>
              <Button
                variant="secondary"
                onClick={() => setPassword(generatePassword())}
              >
                Gerar
              </Button>
            </div>
            {setTemporaryPassword.isError && (
              <p className="text-sm text-red-600 dark:text-red-400">
                Falha ao definir a senha temporária.
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
